'use client';

/**
 * LensCard — what this screen means for the current persona.
 *
 * A small piece of glass anchored in the scene's free area (LENS_ANCHOR,
 * measured against the canonical PNG). Reads everything from useLiving() and
 * the copy book (LENS_BOOK / PERSONA_META).
 *
 * Densities (MOTION-SPEC §8): the scene decides how much is shown at rest
 * (full / compact / strip / pill); any smaller density expands to `full`
 * from the same corner on hover (400 ms, fine pointers), on keyboard focus,
 * or on a tap of the card surface, and folds back on Esc, the toggle, a scene
 * change, an outside pointerdown or when pointer and focus both leave. The
 * toggle (`sn-lens-toggle`) minimises the card to a pill; that choice is kept
 * in sessionStorage `sn-lens-collapsed` for the session.
 *
 * Motion is transform/opacity only, driven by the Web Animations API so a
 * persona swap can exit (160 ms) before the text changes and enter with a
 * stagger without remounting anything (focus survives). `reducedMotion` from
 * context turns every recipe into a single 200 ms opacity fade.
 *
 * Contract with the shell:
 *   - `sceneId` is the scene shown; `frame` says whether the offset parent is
 *     the constant 1672 / 941 viewport (default) or the scene canvas;
 *   - `variant="inline"` renders a static, full-width block (≤ 720 px layout);
 *     without the prop the card switches by itself at (max-width: 720px);
 *   - window events: `sn:spotlight` {hotspot, source:'lens'} while the "try
 *     this" button is hovered / focused (null on leave), `sn:activate`
 *     {hotspot} when no `[data-hotspot]` element exists to click; listens to
 *     `sn:lens-close` to fold the expanded card.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { REFERENCE_SCENES, type ReferenceSceneId } from '../../reference/types';
import { useLiving } from '../context';
import { LENS_BOOK, PERSONA_META } from '../personas';
import type { Persona } from '../types';
import { LENS_ANCHOR, lensCorner, lensSlotStyle, type LensDensity, type LensFrame } from './anchors';
import { LENS_COPY, type LensCopyKey } from './copy';
import styles from './LensCard.module.css';

export { LENS_ANCHOR, LENS_MAX_WIDTH, lensCorner, lensExpandedWidth, lensSlotStyle } from './anchors';
export type { LensAnchor, LensAnchorSpec, LensDensity, LensFrame } from './anchors';
export { LENS_COPY } from './copy';

export const LENS_STORAGE_KEY = 'sn-lens-collapsed';
/** Dispatched on window while the "try this" button is hovered or focused; `hotspot: null` when it is left. */
export const LENS_SPOTLIGHT_EVENT = 'sn:spotlight';
/** Dispatched on window when the "try this" hotspot has no `[data-hotspot]` element to click. */
export const LENS_ACTIVATE_EVENT = 'sn:activate';
/** Listened to on window: folds the expanded card (for a shell that owns the Esc stack). */
export const LENS_CLOSE_EVENT = 'sn:lens-close';

export interface LensSpotlightDetail {
  hotspot: string | null;
  source: 'lens';
}

export interface LensActivateDetail {
  hotspot: string;
  source: 'lens';
}

export interface LensCardProps {
  sceneId: ReferenceSceneId;
  /** Box the card is positioned in. Default: the constant 1672 / 941 viewport. */
  frame?: LensFrame;
  /** Force the layout; without it the card is anchored above 720 px and inline below. */
  variant?: 'anchored' | 'inline';
  className?: string;
}

type EnterKind = 'boot' | 'scene' | 'persona' | 'expand' | 'fade';

const HOVER_OPEN_MS = 400;
const LEAVE_CLOSE_MS = 320;
const FOCUS_SUPPRESS_MS = 260;
const EASE_ENTER = 'cubic-bezier(.16,1,.3,1)';
const EASE_EXIT = 'cubic-bezier(.4,0,.2,1)';
const LINE_STAGGER_CAP = 6;

const GLYPH: Record<Persona, string> = {
  teacher: 'M2.5 3.5h11v7.5h-11zM8 11v2.5M5.5 13.5h5',
  expert: 'M7 11.2a4.2 4.2 0 1 0 0-8.4 4.2 4.2 0 0 0 0 8.4zM10.2 10.2 14 14',
  parent: 'M8 13.5 3.2 8.9a2.9 2.9 0 0 1 4.1-4.1L8 5.5l.7-.7a2.9 2.9 0 0 1 4.1 4.1Z',
  investor: 'M2.5 12.5 6.5 8.5l2.5 2.5 4.5-5M10.5 6h3v3',
};

function readCollapsed(): boolean {
  try {
    return window.sessionStorage.getItem(LENS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    window.sessionStorage.setItem(LENS_STORAGE_KEY, value ? '1' : '0');
  } catch {
    // Storage may be unavailable (private mode, quota); the session simply forgets.
  }
}

function canAnimate(element: Element | null): element is HTMLElement {
  return Boolean(element) && typeof (element as HTMLElement).animate === 'function';
}

function lineElements(card: HTMLElement): HTMLElement[] {
  return Array.from(card.querySelectorAll<HTMLElement>('[data-line]'));
}

/** The element that activates a hotspot: the topmost `[data-hotspot]` (inset controls come after the hit-map). */
function findHotspot(id: string): HTMLElement | null {
  if (!/^[\w-]+$/.test(id)) return null;
  const candidates = Array.from(document.querySelectorAll<HTMLElement>(`[data-hotspot="${id}"]`))
    .filter((element) => !element.closest('[inert]'));
  return candidates.length > 0 ? candidates[candidates.length - 1] : null;
}

function PersonaGlyph({ persona }: { persona: Persona }) {
  return (
    <svg className={styles.glyph} viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d={GLYPH[persona]} />
    </svg>
  );
}

function ToggleGlyph({ collapsed }: { collapsed: boolean }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
      {collapsed ? <path d="M8 4v8M4 8h8" /> : <path d="M4 8h8" />}
    </svg>
  );
}

function ArrowGlyph() {
  return (
    <svg className={styles.arrow} viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" />
    </svg>
  );
}

export default function LensCard({ sceneId, frame = 'viewport', variant, className }: LensCardProps) {
  const { lang, persona, spotlight, openAssistant, dismissNudge, reducedMotion, touch } = useLiving();

  const slotRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const animationsRef = useRef<Animation[]>([]);
  const pendingRef = useRef<EnterKind | null>(null);
  const sequenceRef = useRef(0);
  const hoverTimerRef = useRef<number | null>(null);
  const leaveTimerRef = useRef<number | null>(null);
  const blurTimerRef = useRef<number | null>(null);
  const hoveringRef = useRef(false);
  const focusInsideRef = useRef(false);
  const suppressFocusUntilRef = useRef(0);
  const spotlitRef = useRef<string | null>(null);
  const previousSceneRef = useRef(sceneId);

  const [ready, setReady] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [shownPersona, setShownPersona] = useState<Persona>(persona);
  const [narrow, setNarrow] = useState(false);

  const inline = variant ? variant === 'inline' : narrow;
  const anchor = LENS_ANCHOR[sceneId];
  const restDensity: LensDensity = inline ? 'full' : anchor.density;
  const density: LensDensity = expanded ? 'full' : collapsed ? 'pill' : restDensity;
  const canExpand = density !== 'full';
  const copy = LENS_BOOK[sceneId][shownPersona];
  const meta = PERSONA_META.find((item) => item.id === shownPersona) ?? PERSONA_META[0];
  const sceneIndex = REFERENCE_SCENES.indexOf(sceneId);
  const sceneNumber = String(sceneIndex + 1).padStart(2, '0');
  const t = (key: LensCopyKey) => LENS_COPY[key][lang];
  const tryHotspot = copy.tryThis.hotspot;
  const tryLabel = copy.tryThis.label[lang];

  const cancelAnimations = useCallback(() => {
    for (const animation of animationsRef.current) {
      try {
        animation.cancel();
      } catch {
        // An already-finished animation may refuse; nothing to undo then.
      }
    }
    animationsRef.current = [];
  }, []);

  const clearTimer = (ref: { current: number | null }) => {
    if (ref.current !== null) {
      window.clearTimeout(ref.current);
      ref.current = null;
    }
  };

  const clearTimers = useCallback(() => {
    clearTimer(hoverTimerRef);
    clearTimer(leaveTimerRef);
    clearTimer(blurTimerRef);
  }, []);

  const suppressFocusExpand = () => {
    suppressFocusUntilRef.current = performance.now() + FOCUS_SUPPRESS_MS;
  };

  const emitSpotlight = useCallback((hotspot: string | null) => {
    if (spotlitRef.current === hotspot) return;
    spotlitRef.current = hotspot;
    window.dispatchEvent(new CustomEvent<LensSpotlightDetail>(LENS_SPOTLIGHT_EVENT, { detail: { hotspot, source: 'lens' } }));
  }, []);

  const openExpanded = useCallback(() => {
    clearTimer(hoverTimerRef);
    clearTimer(leaveTimerRef);
    pendingRef.current = 'expand';
    setExpanded(true);
  }, []);

  const closeExpanded = useCallback(() => {
    clearTimer(hoverTimerRef);
    clearTimer(leaveTimerRef);
    pendingRef.current = 'expand';
    setExpanded(false);
  }, []);

  /* Layout mode (≤ 720 px) unless the shell forces a variant. */
  useEffect(() => {
    if (variant) return;
    const media = window.matchMedia('(max-width: 720px)');
    const sync = () => setNarrow(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, [variant]);

  /* First paint: remembered state, boot vs scene entrance. */
  useLayoutEffect(() => {
    setCollapsed(readCollapsed());
    const boot = Boolean(slotRef.current?.closest('[data-boot]'));
    pendingRef.current = boot ? 'boot' : 'scene';
    setReady(true);
  }, []);

  /* Scene change while mounted: fold, land on the current persona, re-enter after the crossfade. */
  useLayoutEffect(() => {
    if (previousSceneRef.current === sceneId) return;
    previousSceneRef.current = sceneId;
    sequenceRef.current += 1;
    clearTimers();
    setExpanded(false);
    setShownPersona(persona);
    pendingRef.current = 'scene';
  }, [sceneId, persona, clearTimers]);

  /* Persona change: lines exit 160 ms, copy swaps, lines re-enter (see the animation effect). */
  useEffect(() => {
    if (shownPersona === persona) return;
    const card = cardRef.current;
    if (!ready || reducedMotion || !canAnimate(card)) {
      pendingRef.current = ready ? 'fade' : pendingRef.current;
      setShownPersona(persona);
      return;
    }
    sequenceRef.current += 1;
    const sequence = sequenceRef.current;
    cancelAnimations();
    const exits = lineElements(card)
      .filter(canAnimate)
      .map((line) => line.animate(
        [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(-6px)' }],
        { duration: 160, easing: EASE_EXIT, fill: 'forwards' },
      ));
    animationsRef.current = exits;
    let stale = false;
    Promise.all(exits.map((animation) => animation.finished))
      .then(() => {
        if (stale || sequence !== sequenceRef.current) return;
        pendingRef.current = 'persona';
        setShownPersona(persona);
      })
      .catch(() => undefined);
    return () => {
      stale = true;
    };
  }, [persona, shownPersona, ready, reducedMotion, cancelAnimations]);

  /* Entrance recipes. Runs after the DOM shows the new copy / density. */
  useLayoutEffect(() => {
    if (!ready) return;
    const kind = pendingRef.current;
    if (!kind) return;
    pendingRef.current = null;
    const card = cardRef.current;
    if (!canAnimate(card)) return;
    cancelAnimations();
    const animations: Animation[] = [];
    if (reducedMotion) {
      if (kind !== 'boot') {
        animations.push(card.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease', fill: 'backwards' }));
      }
    } else if (kind === 'boot') {
      const tx = inline ? 0 : anchor.side === 'left' ? -12 : 12;
      const ty = inline ? 12 : anchor.edge === 'top' ? -12 : 12;
      animations.push(card.animate(
        [{ opacity: 0, transform: `translate(${tx}px, ${ty}px)` }, { opacity: 1, transform: 'none' }],
        { duration: 480, delay: 760, easing: EASE_ENTER, fill: 'backwards' },
      ));
    } else {
      const base = kind === 'scene' ? 560 : 0;
      const step = kind === 'scene' ? 90 : kind === 'persona' ? 70 : 40;
      const duration = kind === 'expand' ? 240 : 420;
      if (kind === 'scene') {
        animations.push(card.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 420, delay: base, easing: EASE_ENTER, fill: 'backwards' }));
      }
      lineElements(card).forEach((line, index) => {
        if (!canAnimate(line)) return;
        animations.push(line.animate(
          [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }],
          { duration, delay: base + Math.min(index, LINE_STAGGER_CAP) * step, easing: EASE_ENTER, fill: 'backwards' },
        ));
      });
    }
    animationsRef.current = animations;
  }, [ready, sceneId, shownPersona, density, reducedMotion, inline, anchor.side, anchor.edge, cancelAnimations]);

  /* Keep keyboard focus inside the card when the focused control changed density. */
  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot || !focusInsideRef.current) return;
    if (slot.contains(document.activeElement)) return;
    suppressFocusExpand();
    toggleRef.current?.focus({ preventScroll: true });
  }, [density]);

  /* Expanded: fold on Esc, outside pointerdown, or the shell's close event. */
  useEffect(() => {
    if (!expanded) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!slotRef.current?.contains(event.target as Node | null)) closeExpanded();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeExpanded();
    };
    const onClose = () => closeExpanded();
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener(LENS_CLOSE_EVENT, onClose);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener(LENS_CLOSE_EVENT, onClose);
    };
  }, [expanded, closeExpanded]);

  /* Unmount: nothing keeps running, and a lens-driven spotlight is released. */
  useEffect(() => () => {
    sequenceRef.current += 1;
    cancelAnimations();
    clearTimers();
    if (spotlitRef.current !== null) {
      spotlitRef.current = null;
      window.dispatchEvent(new CustomEvent<LensSpotlightDetail>(LENS_SPOTLIGHT_EVENT, { detail: { hotspot: null, source: 'lens' } }));
    }
  }, [cancelAnimations, clearTimers]);

  const onCardPointerEnter = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (inline || touch || event.pointerType === 'touch') return;
    hoveringRef.current = true;
    clearTimer(leaveTimerRef);
    if (canExpand && hoverTimerRef.current === null) {
      hoverTimerRef.current = window.setTimeout(() => {
        hoverTimerRef.current = null;
        openExpanded();
      }, HOVER_OPEN_MS);
    }
  };

  const onCardPointerLeave = () => {
    hoveringRef.current = false;
    clearTimer(hoverTimerRef);
    if (!expanded || slotRef.current?.matches(':focus-within')) return;
    leaveTimerRef.current = window.setTimeout(() => {
      leaveTimerRef.current = null;
      closeExpanded();
    }, LEAVE_CLOSE_MS);
  };

  const onCardFocus = (event: ReactFocusEvent<HTMLDivElement>) => {
    focusInsideRef.current = true;
    clearTimer(leaveTimerRef);
    clearTimer(blurTimerRef);
    if (inline || !canExpand) return;
    if (performance.now() < suppressFocusUntilRef.current) return;
    if (!event.target.matches(':focus-visible')) return;
    openExpanded();
  };

  const onCardBlur = (event: ReactFocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null;
    if (next && slotRef.current?.contains(next)) return;
    clearTimer(blurTimerRef);
    blurTimerRef.current = window.setTimeout(() => {
      blurTimerRef.current = null;
      const slot = slotRef.current;
      if (!slot || slot.matches(':focus-within')) return;
      focusInsideRef.current = false;
      if (expanded && !hoveringRef.current) closeExpanded();
    }, 0);
  };

  const onCardClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if ((event.target as Element).closest('button, a')) return;
    if (canExpand) openExpanded();
  };

  const onToggle = () => {
    suppressFocusExpand();
    clearTimers();
    const next = !collapsed;
    writeCollapsed(next);
    pendingRef.current = 'expand';
    setCollapsed(next);
    setExpanded(false);
  };

  const onTryClick = () => {
    dismissNudge();
    const target = findHotspot(tryHotspot);
    if (target) {
      target.click();
      return;
    }
    window.dispatchEvent(new CustomEvent<LensActivateDetail>(LENS_ACTIVATE_EVENT, { detail: { hotspot: tryHotspot, source: 'lens' } }));
  };

  const slotStyle: CSSProperties | undefined = inline ? undefined : lensSlotStyle(sceneId, frame);
  const corner = lensCorner(sceneId);
  const ariaLabel = `${t('lens')}: ${meta.label[lang]}. ${t('scene')} ${sceneIndex + 1} ${t('of')} ${REFERENCE_SCENES.length}`;

  const lines: ReactNode[] = [];
  if (density === 'pill') {
    lines.push(
      <button
        key="pill"
        type="button"
        className={styles.pill}
        data-testid="sn-lens-pill"
        data-line=""
        aria-expanded={false}
        aria-label={`${t('showFull')}: ${copy.headline[lang]}`}
        onClick={openExpanded}
      >
        <PersonaGlyph persona={shownPersona} />
        <span className={styles.pillText}>{copy.headline[lang]}</span>
      </button>,
    );
  } else {
    lines.push(
      <p key="eyebrow" className={styles.eyebrow} data-line="">
        <PersonaGlyph persona={shownPersona} />
        <span>{meta.label[lang]}</span>
        <span className={styles.sep} aria-hidden="true">·</span>
        <span className={styles.index}>{sceneNumber}/{REFERENCE_SCENES.length}</span>
      </p>,
    );
    if (density === 'full') {
      lines.push(<p key="focus" className={styles.focus} data-line="">{meta.focus[lang]}</p>);
    }
    lines.push(<h3 key="headline" className={styles.headline} data-line="">{copy.headline[lang]}</h3>);
    if (density === 'full') {
      lines.push(<p key="insight" className={styles.insight} data-line="">{copy.insight[lang]}</p>);
    }
    lines.push(
      <button
        key="try"
        type="button"
        className={styles.try}
        data-testid="sn-lens-try"
        data-line=""
        data-target={tryHotspot}
        data-active={spotlight === tryHotspot ? 'true' : undefined}
        aria-label={`${t('tryPrefix')}: ${tryLabel}`}
        onPointerEnter={() => emitSpotlight(tryHotspot)}
        onPointerLeave={() => emitSpotlight(null)}
        onFocus={() => emitSpotlight(tryHotspot)}
        onBlur={() => emitSpotlight(null)}
        onClick={onTryClick}
      >
        <ArrowGlyph />
        <span>{tryLabel}</span>
      </button>,
    );
    if (density === 'full') {
      lines.push(
        <div key="questions" className={styles.questions} role="group" aria-label={t('questions')}>
          {copy.questions.map((question, index) => (
            <button
              key={index}
              type="button"
              className={styles.q}
              data-testid={`sn-lens-question-${index + 1}`}
              data-line=""
              aria-label={`${t('askPrefix')}: ${question[lang]}`}
              onClick={() => openAssistant(question[lang])}
            >
              <span className={styles.qMark} aria-hidden="true">?</span>
              <span>{question[lang]}</span>
            </button>
          ))}
        </div>,
      );
    }
  }

  return (
    <aside
      ref={slotRef}
      className={className ? `${styles.slot} ${className}` : styles.slot}
      style={slotStyle}
      data-testid="sn-lens-card"
      data-anchor={corner}
      data-density={density}
      data-expanded={expanded ? 'true' : undefined}
      data-collapsed={collapsed ? 'true' : undefined}
      data-variant={inline ? 'inline' : 'anchored'}
      data-ready={ready ? 'true' : undefined}
      data-persona={shownPersona}
      aria-label={ariaLabel}
    >
      <div
        ref={cardRef}
        className={styles.card}
        onPointerEnter={onCardPointerEnter}
        onPointerLeave={onCardPointerLeave}
        onFocus={onCardFocus}
        onBlur={onCardBlur}
        onClick={onCardClick}
      >
        <div className={styles.body}>{lines}</div>
        <button
          ref={toggleRef}
          type="button"
          className={styles.toggle}
          data-testid="sn-lens-toggle"
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('restore') : t('collapse')}
          onClick={onToggle}
        >
          <ToggleGlyph collapsed={collapsed} />
        </button>
      </div>
    </aside>
  );
}
